import classNames from "classnames";
import {useFieldArray, useFormContext} from "react-hook-form";

import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {useDebouncedSearch} from "@/lib/hooks/useDebouncedSearch.ts";
import {SLAReportGroup, SLAReportGroupInput} from "@/lib/queries/reports";
import {GroupChecksSelector} from "@/pages/checks/components/form/fields/legacy/GroupChecksSelector.tsx";
import {
  deleteGroupsField,
  groupInEditChecksField,
  groupInEditNameField,
  newGroupsField,
} from "@/pages/reports/sla/SLAReportForm.utils.tsx";
import {SLAReportGroupTable} from "@/pages/reports/sla/SLAReportGroupTable.tsx";

interface SLAReportingGroupsSectionProps {
  toggleFieldSelector: () => void;
  defaultGroups: SLAReportGroup[];
}

export const SLAReportingGroupsSection = ({toggleFieldSelector, defaultGroups}: SLAReportingGroupsSectionProps) => {
  const {
    watch,
    control,
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = useFormContext();
  const groupInEditNameError = getFormError(groupInEditNameField, errors);
  const groupInEditName = watch(groupInEditNameField);
  const groupInEditChecks = watch(groupInEditChecksField);
  const {append: appendToAdd, replace: replaceToAdd} = useFieldArray({control, name: newGroupsField});
  const {append: appendToDelete} = useFieldArray({control, name: deleteGroupsField});
  const newGroups = watch(newGroupsField);
  const toDelete = watch(deleteGroupsField);
  const groups = defaultGroups.filter((g) => !(toDelete || []).includes(g.id)).concat(newGroups || []);
  const {search, searchDebounced, searchChanged} = useDebouncedSearch({useUrlParams: false});

  const addGroup = (name: string = "", checks: number[] = []): boolean => {
    let message = "";
    if (!groupInEditName) {
      message = "Name is required";
    }
    if (groups.some((g: SLAReportGroupInput) => g.name === groupInEditName)) {
      message = "Group name must be unique";
    }
    if (message) {
      setError(groupInEditNameField, {message}, {shouldFocus: true});
      return false;
    }

    appendToAdd({name: groupInEditName, group_services: groupInEditChecks});
    setValue(groupInEditNameField, name);
    setValue(groupInEditChecksField, checks);
    return true;
  };

  return (
    <>
      <Card variant="white" className="p-0 mb-4">
        <div className="p-3">
          <div className="d-flex align-items-center">
            <div className="pulseTextBodyBold pulse-color-gray-800">Add New Group</div>
            <TooltipIconRA className="ms-1" title="Group’s uptime % will be an average of Checks’ uptime %." />
          </div>
        </div>
        <Divider />
        <div className="p-3">
          <div className="d-flex">
            <div className="row gx-3 flex-grow-1">
              <div className="col">
                <InputField
                  name={groupInEditNameField}
                  label="Name"
                  maxLength={100}
                  options={{
                    onChange: () => clearErrors(groupInEditNameField),
                  }}
                  required
                />
              </div>
              <div className="col">
                <GroupChecksSelector
                  name={groupInEditChecksField}
                  label="Manually select Checks"
                  tooltip="Select the Checks you would like to include in this group"
                  toggleSubform={toggleFieldSelector}
                />
              </div>
            </div>
            <div className={classNames("ms-3", groupInEditNameError ? "align-content-center" : "align-content-end")}>
              <Button onClick={() => addGroup()} disabled={!!groupInEditNameError || !groupInEditName}>
                Add
              </Button>
            </div>
          </div>
        </div>
      </Card>
      <Card variant="white" className="p-0">
        <div className="p-3">
          <div className="row align-items-center">
            <div className="col pulseTextBodyBold pulse-color-gray-800">List of Groups</div>
            <div className="col">
              <SearchInput value={search} onChange={searchChanged} />
            </div>
          </div>
        </div>
        <Divider />
        <div className="px-3 pt-3">
          <SLAReportGroupTable
            isFiltered={!!searchDebounced}
            groups={groups.filter(({name}) => name.toLowerCase().includes(searchDebounced.toLowerCase()))}
            onEdit={(group) => {
              if (groupInEditName || groupInEditChecks?.length) {
                const added = addGroup(group.name, group.group_services);
                if (!added) {
                  return;
                }
              } else {
                setValue(groupInEditNameField, group.name);
                setValue(groupInEditChecksField, group.group_services);
              }
              if ("id" in group) {
                appendToDelete(group.id);
              } else {
                replaceToAdd(newGroups.filter((g: SLAReportGroupInput) => g.name !== group.name));
              }
            }}
            onDelete={(group) => {
              if ("id" in group) {
                appendToDelete(group.id);
              } else {
                replaceToAdd(newGroups.filter((g: SLAReportGroupInput) => g.name !== group.name));
              }
            }}
          />
        </div>
      </Card>
    </>
  );
};
